import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Alert,
  Box,
  Button,
  Flex,
  H1,
  Input,
  LoadableButton,
  Text
} from '@urbaninfrastructure/react-ui-kit';
import { MailAlt as MailAltIcon } from '@urbaninfrastructure/react-icons';
import { useSystem } from '../lib/core/useSystem';
import * as Yup from 'yup';
import { Formik, Form, FieldProps, Field, FormikErrors } from 'formik';
import { iconSize } from '../lib/constants';
import {
  CURRENT_USER_EMAIL_CONFIRMED_QUERY,
  REQUEST_EMAIL_CONFIRMATION_MUTATION
} from '../modules/Onboarding/graphql';
import {
  currentUserEmailConfirmedQuery,
  requestEmailConfirmation,
  requestEmailConfirmationVariables
} from '../core-types';
import { useMutation, useQuery } from '@apollo/client';
import { useToasts } from '../components/Toasts';

interface Values {
  email: string;
}

interface SubmitEmailStepProps {
  goNext: () => void;
}

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email()
    .required()
});

export const SubmitEmailStep = ({ goNext }: SubmitEmailStepProps) => {
  const system = useSystem();
  const { addToast } = useToasts();
  const [emailSent, setEmailSent] = React.useState<boolean>(false);
  const [polling, setPolling] = React.useState<boolean>(false);
  const [errorAfterTimeout, setErrorAfterTimeout] = React.useState<boolean>(
    false
  );
  const [goNextCalled, setGoNextCalled] = React.useState<boolean>(false);

  const [
    requestEmailConfirmation,
    {
      loading: requestEmailConfirmationLoading,
      error: requestEmailConfirmationError,
      reset: resetRequestEmailConfirmationError
    }
  ] = useMutation<requestEmailConfirmation, requestEmailConfirmationVariables>(
    REQUEST_EMAIL_CONFIRMATION_MUTATION,
    {
      onCompleted: () => {
        if (requestEmailConfirmationError) {
          setEmailSent(false);
          stopPolling();
        } else {
          addToast({
            text: (
              <FormattedMessage
                id="email_has_been_sent"
                defaultMessage="Email has been sent!"
              />
            ),
            type: 'success'
          });
          setEmailSent(true);
          setPolling(true);
          // Timeout to start polling for user data
          setTimeout(() => {
            startPollingUserData();
            // Stop polling after 30 seconds
            stopPollingAfterTimeout();
          }, 10000);
        }
      }
    }
  );

  const {
    data: currentUserData,
    error: currentUserError,
    startPolling,
    stopPolling
  } = useQuery<currentUserEmailConfirmedQuery>(
    CURRENT_USER_EMAIL_CONFIRMED_QUERY,
    {
      skip: !emailSent
    }
  );

  const startPollingUserData = () => {
    // Poll interval is 5 seconds
    startPolling(5000);
    stopPollingAfterTimeout();
  };

  const stopPollingAfterTimeout = () => {
    setTimeout(() => {
      stopPolling();
      setPolling(false);
      setErrorAfterTimeout(true);
    }, 300000);
  };

  React.useEffect(() => {
    if (currentUserData?.currentUser?.emailConfirmed && !goNextCalled) {
      stopPolling();
      setPolling(false);
      setGoNextCalled(true);
      goNext();
    }
  }, [currentUserData?.currentUser?.emailConfirmed, goNext, goNextCalled]);

  const handleEmailConfirmedClick = () => {
    setEmailSent(true);
    setPolling(true);
    setErrorAfterTimeout(false);
    resetRequestEmailConfirmationError();
    startPollingUserData();
  };

  if (!system) {
    throw new Error('System not found');
  }

  const isLoading = requestEmailConfirmationLoading || polling;

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      maxWidth="500px"
      textAlign="center"
    >
      <MailAltIcon size={iconSize} color="primaryIcon" mb={6} />
      {(requestEmailConfirmationError ||
        currentUserError ||
        errorAfterTimeout) && (
        <Box mb={6}>
          <Alert variant="error">
            <FormattedMessage
              id="error_generic_try_again"
              defaultMessage={'Something went wrong. Please try again.'}
            />
          </Alert>
        </Box>
      )}
      <H1 typoStyle="lg">
        <FormattedMessage
          id="confirm_your_email"
          defaultMessage="Confirm your email"
        />
      </H1>
      <Text mb={4}>
        <FormattedMessage
          id="confirm_your_email_description"
          defaultMessage={
            'Please confirm your email address by entering it in the input field and clicking the button below.'
          }
        />
      </Text>
      <Text mb={42}>
        <FormattedMessage
          id="confirm_your_email_instructions"
          defaultMessage={
            'You will receive an email with instructions on how to activate your account.'
          }
        />
      </Text>
      <Box mb={20}>
        <Formik<Values>
          initialValues={{ email: '' }}
          validationSchema={validationSchema}
          validate={() => {
            const errors: FormikErrors<Values> = {};

            return errors;
          }}
          onSubmit={values => {
            requestEmailConfirmation({
              variables: {
                email: values.email
              }
            });
          }}
        >
          {({ handleSubmit, touched, errors }) => (
            <Form onSubmit={handleSubmit}>
              <Box mb="4">
                <Field name="email">
                  {({ field }: FieldProps<Values['email'], Values>) => (
                    <Input
                      {...field}
                      disabled={isLoading}
                      id="user-email"
                      data-testid="Email_Confirmation_Input"
                      placeholder="email"
                      autoComplete="email"
                      type="email"
                    />
                  )}
                </Field>
                {errors.email && touched.email && (
                  <Text
                    color="state.error"
                    mt="1"
                    textAlign="left"
                    fontSize={16}
                  >
                    {errors.email}
                  </Text>
                )}
              </Box>
              <Flex flexDirection={'column'}>
                <LoadableButton
                  type="submit"
                  shape="cool"
                  variant="primary"
                  loading={isLoading}
                >
                  <Text>
                    {requestEmailConfirmationError ? (
                      <FormattedMessage
                        id="resend_email_confirmation"
                        defaultMessage="Resend email"
                      />
                    ) : (
                      <FormattedMessage
                        id="confirm_email"
                        defaultMessage="Confirm email"
                      />
                    )}
                  </Text>
                </LoadableButton>
                <Button
                  variant="link"
                  onClick={handleEmailConfirmedClick}
                  disabled={isLoading}
                >
                  <Text>
                    <FormattedMessage
                      id="i_have_confirmed_my_email"
                      defaultMessage="I have confirmed my email"
                    />
                  </Text>
                </Button>
              </Flex>
            </Form>
          )}
        </Formik>
      </Box>
    </Flex>
  );
};
